import {memo} from 'react';

import styled from 'styled-components';

import {FlexCenter} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import {getLocalizationService} from '~/shared/services/localisationService';
import {headingXS} from '~/shared/theme/typography';
import PiePlusIcon from '~/assets/images/icons/pie/functionalityPlusSmall.svg?react';
import PieButton from '~/shared/theme/pie/PieButton';
import {flipOnLTR} from '~/shared/theme/utils';
import {capitalFirstLetterAfterSpace} from '~/shared/utils/general';
import {getLegacySiteUrl} from '~/shared/services/urlService';
import TenbisIcon from '~/shared/utils/payments/cardsPie/moneycardSmall.svg?react';

import EmptyStateImage from './emptyStateImage.svg?react';

const Root = styled(FlexCenter)`
  flex-direction: column;
  padding: 24px 0;
  gap: 24px;
  ${media.minLargeMobile`
    padding: 16px 0;
  `}
`;

const StyledEmptyStateImage = styled(EmptyStateImage).attrs<{isMinLargeMobile: boolean}>(({isMinLargeMobile}) => ({
  width: isMinLargeMobile ? 200 : 180,
  height: isMinLargeMobile ? 194 : 174,
  viewBox: '0 0 180 174',
}))``;

const Label = styled.p`
  ${headingXS}
  color: ${({theme}) => theme.colors.charcoal80};
  text-align: center;
`;

const AddPaymentButton = styled(PieButton).attrs({variant: 'primaryLarge'})`
  width: 248px;
  display: flex;
  align-items: center;
  padding: 10px 24px;
`;

const StyledPiePlusIcon = styled(PiePlusIcon)`
  margin-bottom: 2px;
  ${flipOnLTR`
    margin-left: 6px;
  `}
`;

const ManageTenbisCardButton = styled(PieButton).attrs({
  variant: 'outlineLarge',
  forwardedAs: 'a' as const,
  href: getLegacySiteUrl('Account/UserAccountManagement'),
})`
  width: 248px;

  svg {
    ${flipOnLTR`
      margin-left: 8px;
    `}
  }
`;

type PaymentsEmptyStateProps = {
  onAddPaymentClick: () => void;
  isMinLargeMobile: boolean;
  isB2B: boolean;
};

const PaymentsEmptyState = ({onAddPaymentClick, isMinLargeMobile, isB2B}: PaymentsEmptyStateProps) => {
  const {t} = getLocalizationService();

  const addPaymentButtonProps = isB2B
    ? {
        forwardedAs: 'a' as const,
        href: getLegacySiteUrl('/Moneycard/MoneycardOtlRuleManagement'),
      }
    : {
        onClick: onAddPaymentClick,
      };
  
  return (
    <Root>
      <StyledEmptyStateImage isMinLargeMobile={isMinLargeMobile} />
      <Label>
        {t('personal_area_page_payments_empty_state_label')}
      </Label>
      <AddPaymentButton data-test-id="addPaymentBtn" {...addPaymentButtonProps}>
        <StyledPiePlusIcon />
        {capitalFirstLetterAfterSpace(t('add_payment_method'))}
      </AddPaymentButton>
      {isMinLargeMobile && isB2B && (
        <ManageTenbisCardButton>
          <TenbisIcon />
          {t('tenbis_card_management')}
        </ManageTenbisCardButton>
      )}
    </Root>
  );
};

export default memo(PaymentsEmptyState);
